import { parse, ParseResult } from 'papaparse';
import { isSmmParameterIndex, Parameter, parametersCompareFn } from './parameter';
import { Optional } from './types';

export type ConfigParameter = Optional<Parameter, 'name' | 'typeValueKey' | 'id'>;

/**
 * Whether a configuration file has to be loaded through the SMM Setup Wizard rather than through the
 * ordinary drive parameter path.
 *
 * Two things decide it. A configuration only needs the wizard if it carries safety parameters at all,
 * and the wizard only applies to a device that keeps its safety configuration on a detachable module —
 * where those parameters live in a separate parameter space, which is what the wizard and its
 * verification and validation steps exist for.
 *
 * A device with no such module keeps its safety parameters in its own object dictionary and writes them
 * into the same config.csv as everything else, so for it they are drive parameters. That is a Circulo NG:
 * sending the operator to the wizard would send that configuration somewhere it does not belong, and
 * leave the drive with no way to be configured from a file at all.
 *
 * `deviceSupportsSafetyModule` comes from {@link supportsSafetyModule}, which reads the device's own
 * dictionary. Pass `true` when it is not known: refusing a load that would have been allowed is
 * recoverable, and loading safety parameters down a path that cannot verify them is not.
 */
export function requiresSmmSetupWizard(
  parameterIndexes: readonly number[],
  deviceSupportsSafetyModule: boolean
): boolean {
  if (!parameterIndexes.some(isSmmParameterIndex)) {
    return false;
  }
  return deviceSupportsSafetyModule;
}

/**
 * Parses the contents of configuration files such as config.csv, .factory_config, and .assembly_config.
 */
export class ConfigFile {
  readonly apiId?: string;
  readonly firmwareVersion?: string;
  readonly header?: string;
  readonly parseResult?: ParseResult<string[]>;

  parameters: ConfigParameter[];

  constructor(
    public readonly content: string,
    public readonly file?: Pick<File, 'name' | 'type' | 'size' | 'lastModified'>
  ) {
    const normalizedContent = content.replace(/[\r\n]+/g, '\n');

    this.parseResult = parse<string[]>(normalizedContent, {
      skipEmptyLines: true,
      transform: (value: string) => value.trim(),
    });

    if (this.parseResult?.errors?.length > 0) {
      throw new Error('Failed to parse the contents of the configuration CSV file.');
    }

    const matchApiId = normalizedContent.match(/#\s+(\d{4}-\d{2}(-\d{4})?(-\d+)*)$/m);
    if (matchApiId) {
      this.apiId = matchApiId[1];
    }

    const matchFirmwareVersion = normalizedContent.match(/^#\s+(v.*)$/m);
    if (matchFirmwareVersion) {
      this.firmwareVersion = matchFirmwareVersion[1];
    }

    const matchFirstParameterRow = normalizedContent.match(/^0x\d{4}/m);
    if (matchFirstParameterRow?.index) {
      this.header = normalizedContent.slice(0, matchFirstParameterRow.index - 1);
    }

    this.parameters = this.parseResult.data
      .filter(([c0]) => c0.startsWith('0x'))
      .map(([c0, c1, c2]) => {
        const index = parseInt(c0, 16);
        const subindex = parseInt(c1, 10);
        const numValue = Number(c2);
        const value = isNaN(numValue) ? c2 : numValue;
        return { index, subindex, value };
      })
      .sort(parametersCompareFn);
  }

  get firmwareId(): string | undefined {
    return this.apiId;
  }

  /**
   * Whether the file carries any safety parameter. Says nothing about how it has to be loaded —
   * see {@link requiresSmmSetupWizard} for that.
   */
  get containsSmmParameters(): boolean {
    return this.parameters.some((parameter) => isSmmParameterIndex(parameter.index));
  }

  /**
   * Whether this file has to be loaded through the SMM Setup Wizard on a device that keeps its safety
   * configuration on a detachable module. See {@link requiresSmmSetupWizard}.
   */
  requiresSmmSetupWizard(deviceSupportsSafetyModule: boolean): boolean {
    return requiresSmmSetupWizard(
      this.parameters.map((parameter) => parameter.index),
      deviceSupportsSafetyModule
    );
  }

  get hasParseErrors(): boolean {
    return (this.parseResult?.errors.length ?? 0) > 0;
  }

  get numberOfParameters(): number {
    return this.parameters?.length ?? 0;
  }

  toString(): string {
    let out = this.parameters
      .map((p) => `0x${p.index?.toString(16)}, ${p.subindex?.toString(10)}, ${p.value}`)
      .join('\n');
    if (this.header) {
      out = this.header + '\n' + out;
    }
    return out + '\n';
  }
}
